export type OrderingDiagnostic<Context extends string> = {
  earlierRuleId: number;
  contexts: readonly Context[];
};

type OrderedRule = { id: number; order: number };
type ConditionId = number | string | null | undefined;
type RuleNode<Rule extends OrderedRule> = {
  rules: readonly Rule[];
  children: readonly RuleNode<Rule>[];
};

/**
 * Find only exact-condition and earlier-unconditional overlaps among siblings.
 * Context population and condition identity are supplied by the owning domain.
 */
export function orderingDiagnostics<Rule extends OrderedRule, Context extends string>(
  rules: readonly Rule[],
  contextIds: readonly Context[],
  conditionIds: (rule: Rule) => readonly ConditionId[],
  isContextPopulated: (rule: Rule, context: Context) => boolean,
): ReadonlyMap<number, readonly OrderingDiagnostic<Context>[]> {
  const ordered = [...rules].sort((left, right) => left.order - right.order || left.id - right.id);
  const result = new Map<number, OrderingDiagnostic<Context>[]>();

  const sameConditions = (left: Rule, right: Rule) => {
    const leftConditions = conditionIds(left);
    const rightConditions = conditionIds(right);
    return (
      leftConditions.length === rightConditions.length &&
      leftConditions.every((condition, index) => (condition ?? null) === (rightConditions[index] ?? null))
    );
  };
  const isUnconditional = (rule: Rule) => conditionIds(rule).every((condition) => condition == null);

  ordered.forEach((later, laterIndex) => {
    for (const earlier of ordered.slice(0, laterIndex)) {
      if (!sameConditions(earlier, later) && !isUnconditional(earlier)) continue;
      const overlapping = contextIds.filter(
        (context) => isContextPopulated(earlier, context) && isContextPopulated(later, context),
      );
      if (!overlapping.length) continue;
      const diagnostics = result.get(later.id) ?? [];
      diagnostics.push({ earlierRuleId: earlier.id, contexts: overlapping });
      result.set(later.id, diagnostics);
    }
  });

  return result;
}

/** Index endpoint-local diagnostics for every rule in a sparse authored tree. */
export function indexOrderingDiagnostics<Rule extends OrderedRule, Diagnostic>(
  nodes: readonly RuleNode<Rule>[],
  diagnosticsForRules: (rules: readonly Rule[]) => ReadonlyMap<number, readonly Diagnostic[]>,
): ReadonlyMap<number, readonly Diagnostic[]> {
  const result = new Map<number, readonly Diagnostic[]>();
  const visit = (node: RuleNode<Rule>) => {
    const diagnostics = diagnosticsForRules(node.rules);
    node.rules.forEach((rule) => result.set(rule.id, diagnostics.get(rule.id) ?? []));
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return result;
}
